import { useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { activeClientState, dataVersionState } from '../state.js';
import { api } from '../api.js';
import { useApi } from '../hooks.js';
import { Card, EmptyState } from '../components/Card.js';
import { FundLogo } from '../components/FundChip.js';
import { Loading } from '../components/Loading.js';
import { Switch } from '../components/ui/switch.js';
import { Modal } from '../components/ui/modal.js';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select.js';
import { Table, THeadRow, TH, TD, TR, Ltr, IdText } from '../components/ui/table.js';
import { cn, fmtMonthLong, fmtNum } from '../utils.js';
import { SnapshotsCard } from '../components/admin/SnapshotsCard.js';
import { FundsCard } from '../components/admin/FundsCard.js';
import { pdfBatch } from '../components/admin/pdfBatchApi.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { ErrorBanner } from '../components/FormBits.js';
import type { DataQualityIssue } from '@agio/core';

/** As the admin API returns it: the stored issue, plus what to do about it. */
type IssueView = DataQualityIssue & { fix?: string | null; check_prompt?: string | null };

interface AdminClient {
  client_id: string;
  display_name_he: string;
  group_id: string;
  /** First month ('YYYY-MM') the client's return charts show; null = from the beginning. */
  returns_from_month: string | null;
}

interface Job {
  job_id: string;
  client_id: string;
  kind: string;
  filename: string;
  status: string;
  /** batch_id is present only for uploads that went through review — the only
   *  ones carrying the stamps an undo needs. */
  summary: { counts?: Record<string, number>; batch_id?: string; rolled_back?: boolean };
  created_at: string;
}

/**
 * The verification screens, rendered one at a time by the admin shell.
 *
 * These used to be a tab strip on their own page, separate from the upload that
 * produces the data they verify. They are now sections of one admin area — same
 * cards, chosen from the side nav instead.
 */
export type DataSection = 'clients' | 'funds' | 'jobs' | 'snapshots' | 'issues';

/** Badge counts for the side nav, so it can show what needs attention — for the
 *  client picked in the header, like the screens they lead to. Problems that
 *  belong to no client count too: the issues screen shows them alongside. */
export function useAdminDataCounts(): { jobs: number; errors: number } {
  const clientId = useRecoilValue(activeClientState);
  const { data: jobs } = useApi<Job[]>('/admin/jobs');
  const { data: issues } = useApi<IssueView[]>('/admin/issues');
  return {
    jobs: (jobs ?? []).filter((j) => j.client_id === clientId).length,
    errors: (issues ?? []).filter((i) => i.severity === 'error' && (!i.client || i.client === clientId)).length,
  };
}

export function AdminDataSection({ section }: { section: DataSection }) {
  const clientId = useRecoilValue(activeClientState) ?? '';
  const { data: clients, loading } = useApi<AdminClient[]>('/admin/clients');
  const { data: jobs } = useApi<Job[]>('/admin/jobs');
  const { data: issues } = useApi<IssueView[]>('/admin/issues');

  // Only on the first load. A refetch (after a rename, say) must not blank the
  // whole page out to a spinner — that reads as the app reloading itself.
  if (loading && !clients) return <Loading />;

  return (
    <>
      {section === 'clients' && <ClientsCard clients={clients ?? []} />}
      {section === 'funds' && <FundsCard />}
      {section === 'jobs' && <JobsCard jobs={(jobs ?? []).filter((j) => j.client_id === clientId)} />}
      {section === 'snapshots' && <SnapshotsCard />}
      {section === 'issues' && (
        <DataQualityCard
          // Keyed so a switch in the header starts the new client on their own
          // issues, with no severity filter left over from the last one.
          key={clientId}
          clientId={clientId}
          issues={(issues ?? []).filter((i) => !i.client || i.client === clientId)}
        />
      )}
    </>
  );
}

/** A UUID, shortened to its first block, with the full value one click away. */
function ShortId({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="text-muted-foreground">—</span>;
  return (
    <button
      type="button"
      title={`${value} — לחצו להעתקה`}
      onClick={async () => {
        await navigator.clipboard.writeText(value).catch(() => {});
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      className="rounded bg-muted/60 px-1.5 py-0.5 transition-colors hover:bg-muted"
    >
      <IdText className={copied ? 'text-foreground' : undefined}>
        {copied ? 'הועתק ✓' : `${value.slice(0, 8)}…`}
      </IdText>
    </button>
  );
}

/**
 * Clients, one row each, editing behind a modal.
 *
 * Rename used to be a hover-only ✎ on the name, and fund visibility was a whole
 * separate tab with its own client dropdown — two places to change one client.
 * Both now live in the row's עריכה dialog.
 */
function ClientsCard({ clients }: { clients: AdminClient[] }) {
  const [editing, setEditing] = useState<AdminClient | null>(null);
  // Keep the open dialog in sync with the list after a rename refetch.
  const live = editing ? clients.find((c) => c.client_id === editing.client_id) ?? editing : null;

  if (clients.length === 0) {
    return (
      <Card>
        <EmptyState label="אין לקוחות עדיין — קלוט חשבון ראשון בעמוד הקליטה" />
      </Card>
    );
  }
  return (
    <Card>
      <Table>
        <THeadRow>
          <TH>שם</TH>
          <TH>מזהה</TH>
          <TH>קבוצה</TH>
          <TH align="end" />
        </THeadRow>
        <tbody>
          {clients.map((c) => (
            <TR key={c.client_id}>
              <TD className="font-medium">{c.display_name_he}</TD>
              <TD><IdText>{c.client_id}</IdText></TD>
              <TD><ShortId value={c.group_id} /></TD>
              <TD align="end">
                <button
                  type="button"
                  onClick={() => setEditing(c)}
                  className="rounded-md border border-border px-3 py-1 text-xs font-medium transition-colors hover:bg-muted"
                >
                  עריכה
                </button>
              </TD>
            </TR>
          ))}
        </tbody>
      </Table>
      {live && <ClientEditModal client={live} onClose={() => setEditing(null)} />}
    </Card>
  );
}

/** Everything about one client: its display name, and which funds the portal shows. */
function ClientEditModal({ client, onClose }: { client: AdminClient; onClose: () => void }) {
  return (
    <Modal
      open
      size="lg"
      title={`עריכת ${client.display_name_he}`}
      description="השינויים נשמרים מיד"
      onClose={onClose}
      footer={
        <button
          type="button"
          onClick={onClose}
          className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
        >
          סגירה
        </button>
      }
    >
      <div className="flex flex-col gap-6">
        <ClientNameField client={client} />
        <ReturnsFromField client={client} />
        <div>
          <h3 className="mb-1 text-sm font-semibold">בתי השקעות</h3>
          <p className="mb-3 text-xs text-muted-foreground">
            בית השקעות שכבוי כאן לא יופיע כלל בפורטל של הלקוח
          </p>
          <FundVisibilityList clientId={client.client_id} />
        </div>
        <HideClientSection client={client} onHidden={onClose} />
      </div>
    </Modal>
  );
}

/**
 * Hiding the client. The soft delete: nothing is erased, but the client leaves
 * every screen — including this one — and its people can no longer sign in.
 *
 * There is deliberately no way back from here. That is what the typed
 * confirmation is for: hiding is one click from a dialog, and the client id has
 * to be typed out before that click does anything.
 */
function HideClientSection({ client, onHidden }: { client: AdminClient; onHidden: () => void }) {
  const bump = useSetRecoilState(dataVersionState);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const hide = async () => {
    setAsking(false);
    setBusy(true);
    setError(null);
    try {
      await api.post(`/admin/clients/${encodeURIComponent(client.client_id)}/hide`, {});
      bump((v) => v + 1);
      onHidden();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'ההסתרה נכשלה — נסו שוב');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4">
      <h3 className="mb-1 text-sm font-semibold text-destructive">הסתרת הלקוח</h3>
      <p className="mb-3 text-xs text-muted-foreground">
        הלקוח יוסר מכל המסכים והמשתמשים שלו לא יוכלו להתחבר. הנתונים עצמם נשמרים
        במלואם, אך אין דרך להחזיר את הלקוח דרך הממשק.
      </p>
      <ErrorBanner error={error} title="ההסתרה נכשלה" />
      <button
        type="button"
        disabled={busy}
        onClick={() => setAsking(true)}
        className="rounded-md border border-red-800 px-3 py-1.5 text-xs font-medium text-red-400 transition-colors hover:bg-red-950/60 disabled:opacity-40"
      >
        {busy ? 'מסתיר…' : 'הסתרת הלקוח'}
      </button>

      <ConfirmDialog
        open={asking}
        title={`להסתיר את ${client.display_name_he}?`}
        confirmLabel="הסתר"
        tone="danger"
        confirmPhrase={client.client_id}
        onConfirm={() => void hide()}
        onCancel={() => setAsking(false)}
      >
        <p>
          <span className="font-semibold">{client.display_name_he}</span> ייעלם מרשימת
          הלקוחות, מהפורטל וממסכי הניהול, והמשתמשים שלו ינותקו ולא יוכלו להתחבר.
        </p>
        <p className="mt-2">
          שום נתון לא יימחק — בתי ההשקעות, הדוחות והתשואות נשארים במסד הנתונים כפי שהם.
        </p>
        <p className="mt-2 font-semibold text-foreground">
          הפעולה אינה הפיכה דרך הממשק: מרגע ההסתרה הלקוח לא יופיע בשום מסך, ולכן גם לא
          ניתן יהיה להחזירו מכאן.
        </p>
      </ConfirmDialog>
    </div>
  );
}

/** The client's Hebrew display name. Saves on Enter or blur, Esc reverts. */
function ClientNameField({ client }: { client: AdminClient }) {
  const bump = useSetRecoilState(dataVersionState);
  const [value, setValue] = useState(client.display_name_he);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(false);
  useEffect(() => { setValue(client.display_name_he); }, [client.display_name_he]);

  const save = async () => {
    const trimmed = value.trim();
    if (!trimmed || trimmed === client.display_name_he) {
      setValue(client.display_name_he);
      return;
    }
    setError(false);
    try {
      await api.patch(`/admin/clients/${encodeURIComponent(client.client_id)}`, { displayName: trimmed });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
      bump((v) => v + 1);
    } catch {
      setValue(client.display_name_he);
      setError(true);
    }
  };

  return (
    <label className="block">
      <span className="mb-1 flex items-center gap-2 text-sm font-semibold">
        שם תצוגה
        {saved && <span className="text-xs font-normal text-emerald-700">נשמר ✓</span>}
        {error && <span className="text-xs font-normal text-destructive">לא נשמר — נסו שוב</span>}
      </span>
      <input
        className="w-full max-w-sm rounded-md border border-input bg-popover px-3 py-2 text-sm"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') setValue(client.display_name_he);
        }}
      />
    </label>
  );
}

const FROM_START = 'start';

/**
 * From which month the client's return charts start. The choice is one of the
 * client's own months, so a month with no data cannot be picked; "מההתחלה"
 * clears it. Saved at once; the portal and the printed report follow it.
 */
function ReturnsFromField({ client }: { client: AdminClient }) {
  const bump = useSetRecoilState(dataVersionState);
  const { data: portfolio } = useApi<{ months: { date: string }[] }>(`/clients/${encodeURIComponent(client.client_id)}/portfolio`);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(false);
  const months = [...new Set((portfolio?.months ?? []).map((m) => m.date.slice(0, 7)))].sort().reverse();
  const current = client.returns_from_month ?? FROM_START;
  // A stored month the series no longer has stays pickable, so the screen never hides what is set.
  const options = current !== FROM_START && !months.includes(current) ? [current, ...months] : months;

  const save = async (v: string) => {
    const returnsFromMonth = v === FROM_START ? null : v;
    if (returnsFromMonth === client.returns_from_month) return;
    setError(false);
    try {
      await api.patch(`/admin/clients/${encodeURIComponent(client.client_id)}`, { returnsFromMonth });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
      bump((n) => n + 1);
    } catch {
      setError(true);
    }
  };

  return (
    <div>
      <span className="mb-1 flex items-center gap-2 text-sm font-semibold">
        הצגת תשואות מחודש
        {saved && <span className="text-xs font-normal text-emerald-700">נשמר ✓</span>}
        {error && <span className="text-xs font-normal text-destructive">לא נשמר — נסו שוב</span>}
      </span>
      <p className="mb-2 text-xs text-muted-foreground">
        גרפי התשואה בפורטל ובדוח המודפס יתחילו מהחודש שנבחר; התשואה המצטברת נספרת מהחודש שלפניו.
      </p>
      <Select dir="rtl" value={current} onValueChange={(v) => void save(v)}>
        <SelectTrigger className="w-full max-w-sm" aria-label="הצגת תשואות מחודש">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={FROM_START}>מההתחלה</SelectItem>
          {options.map((m) => (
            <SelectItem key={m} value={m}>
              {fmtMonthLong(`${m}-01`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

interface AdminFund {
  fund_id: string;
  fund_name: string;
  is_old: boolean;
  is_visible: boolean;
  has_holdings: boolean;
  has_performance: boolean;
}

/** Show/hide funds for one client. ישן funds are hidden by default at ingest;
 * hidden funds are NOT served to the portal (BE-enforced). */
function FundVisibilityList({ clientId }: { clientId: string }) {
  const { data: funds, loading } = useApi<AdminFund[]>(`/admin/clients/${clientId}/funds`);
  // A switch owns its own state: bumping the global data version here would
  // re-fetch every query on the page and drop it back to the spinner.
  const [override, setOverride] = useState<Record<string, boolean>>({});
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => { setOverride({}); setFailed(null); }, [clientId]);

  const isVisible = (f: AdminFund) => override[f.fund_id] ?? f.is_visible;

  const toggle = async (f: AdminFund, next: boolean) => {
    setOverride((o) => ({ ...o, [f.fund_id]: next }));
    setFailed(null);
    try {
      await api.patch(`/admin/funds/${encodeURIComponent(f.fund_id)}`, { isVisible: next });
    } catch {
      setOverride((o) => {
        const { [f.fund_id]: _dropped, ...rest } = o;
        return rest;
      });
      setFailed(f.fund_name);
    }
  };

  if (loading && !funds) return <Loading />;
  if ((funds ?? []).length === 0) return <EmptyState label="אין בתי השקעות" />;

  return (
    <>
      {failed && (
        <p className="mb-3 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          השינוי ל־{failed} לא נשמר. נסו שוב.
        </p>
      )}
      <Table minWidth="26rem">
        <THeadRow>
          <TH>קרן</TH>
          <TH>ישן</TH>
          <TH>נתונים</TH>
          <TH align="end">מוצג ללקוח</TH>
        </THeadRow>
        <tbody>
          {(funds ?? []).map((f) => (
            <TR key={f.fund_id}>
              <TD className="py-2.5">
                <span className="flex items-center gap-2">
                  <FundLogo name={f.fund_name} />
                  {f.fund_name}
                </span>
              </TD>
              <TD className="py-2.5 text-muted-foreground">{f.is_old ? 'ישן' : '—'}</TD>
              <TD className="py-2.5 text-xs text-muted-foreground">
                {[f.has_holdings && 'ריכוז', f.has_performance && 'תשואות'].filter(Boolean).join(' · ') || '—'}
              </TD>
              <TD align="end" className="py-2.5">
                <span className="flex items-center justify-end gap-2">
                  <span className={cn('text-xs font-medium', isVisible(f) ? 'text-emerald-700' : 'text-muted-foreground')}>
                    {isVisible(f) ? 'מוצג' : 'מוסתר'}
                  </span>
                  <Switch
                    checked={isVisible(f)}
                    onChange={(next) => toggle(f, next)}
                    aria-label={`הצגת ${f.fund_name} ללקוח`}
                  />
                </span>
              </TD>
            </TR>
          ))}
        </tbody>
      </Table>
    </>
  );
}

/** The uploads of the client picked in the header. */
function JobsCard({ jobs }: { jobs: Job[] }) {
  const bump = useSetRecoilState(dataVersionState);
  const [undoing, setUndoing] = useState<Job | null>(null);
  const [busy, setBusy] = useState(false);
  const [undone, setUndone] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const undo = async () => {
    if (!undoing) return;
    setBusy(true);
    setError(null);
    try {
      await pdfBatch.rollback(undoing.job_id);
      setUndone((u) => new Set(u).add(undoing.job_id));
      bump((v) => v + 1);
    } catch (err) {
      // A failed undo that says nothing is worse than no undo button at all.
      setError((err as Error).message);
    } finally {
      setBusy(false);
      setUndoing(null);
    }
  };

  if (jobs.length === 0) {
    return (
      <Card>
        <EmptyState label="אין קליטות ללקוח הזה" />
      </Card>
    );
  }
  return (
    <Card>
      <ConfirmDialog
        open={!!undoing}
        title="לבטל את הקליטה?"
        confirmLabel="בטל קליטה"
        onConfirm={() => void undo()}
        onCancel={() => setUndoing(null)}
      >
        <p>
          כל מה שהקליטה הזו כתבה יימחק, ומה שהיא החליפה יוחזר למקומו.
        </p>
        <p className="mt-2 text-muted-foreground">{undoing?.filename}</p>
      </ConfirmDialog>
      <Table minWidth="48rem">
        <THeadRow>
          <TH>סוג</TH>
          <TH>קובץ</TH>
          <TH>סטטוס</TH>
          <TH align="end">רשומות</TH>
          <TH>מתי</TH>
          <TH align="end" />
        </THeadRow>
        <tbody>
          {jobs.map((j) => {
            const counts = Object.entries(j.summary?.counts ?? {});
            const total = counts.reduce((a, [, v]) => a + (v ?? 0), 0);
            return (
              <TR key={j.job_id} className="align-top">
                <TD className="whitespace-nowrap">
                  <JobKindBadge kind={j.kind} />
                </TD>
                <TD className="max-w-[16rem] truncate text-muted-foreground" title={j.filename}>
                  {j.filename}
                </TD>
                <TD className="whitespace-nowrap">
                  {j.status === 'ok'
                    ? <span className="rounded-full bg-emerald-600/15 px-2 py-0.5 text-xs font-semibold text-emerald-700">ok</span>
                    : <span className="rounded-full bg-destructive/15 px-2 py-0.5 text-xs font-semibold text-destructive">{j.status}</span>}
                </TD>
                <TD
                  align="end"
                  className="whitespace-nowrap tabular-nums"
                  title={counts.map(([k, v]) => `${k}: ${fmtNum(v)}`).join('\n')}
                >
                  {total > 0
                    ? <span className="cursor-default border-b border-dotted border-muted-foreground/40">{fmtNum(total)}</span>
                    : <span className="text-muted-foreground">0</span>}
                </TD>
                <TD className="whitespace-nowrap text-xs text-muted-foreground">
                  <Ltr>{new Date(j.created_at).toLocaleString('he-IL')}</Ltr>
                </TD>
                <TD align="end" className="whitespace-nowrap">
                  {/* Only uploads that went through review carry the stamps an
                      undo needs. Workbook ingests, and PDFs from before review
                      existed, are repaired one snapshot at a time under
                      "תמונות מצב" — offering a button that cannot work is worse
                      than offering none. */}
                  {j.summary?.batch_id && !j.summary?.rolled_back && !undone.has(j.job_id) && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => setUndoing(j)}
                      className="rounded-md border border-destructive/40 px-2 py-0.5 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-50"
                    >
                      בטל קליטה
                    </button>
                  )}
                  {(j.summary?.rolled_back || undone.has(j.job_id)) && (
                    <span className="text-xs text-muted-foreground">בוטלה</span>
                  )}
                </TD>
              </TR>
            );
          })}
        </tbody>
      </Table>
      <ErrorBanner error={error} title="ביטול הקליטה נכשל" />
    </Card>
  );
}

const SEVERITY_LABEL = { error: 'שגיאות', warn: 'אזהרות', info: 'מידע' } as const;
const SEVERITY_DOT = { error: 'bg-destructive', warn: 'bg-warning', info: 'bg-muted-foreground/50' } as const;

/**
 * Hands the problem to an AI assistant, with the workbook, without anyone
 * having to describe our parser's rules from memory. The prompt is built on the
 * server and carries no client data — see ingestAdvice.ts.
 */
function CopyCheckPrompt({ prompt }: { prompt: string }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');

  // The clipboard is refused more often than it looks: an unfocused window, an
  // insecure origin, a browser that just says no. A button that quietly does
  // nothing is the worst outcome, so a refusal reveals the text instead and the
  // reader copies it by hand.
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setState('copied');
      setTimeout(() => setState('idle'), 2000);
    } catch {
      setState('failed');
    }
  };

  if (state === 'failed') {
    return (
      <div className="w-full">
        <p className="text-xs text-muted-foreground">
          הדפדפן לא איפשר העתקה אוטומטית — סמנו את הטקסט והעתיקו:
        </p>
        <textarea
          readOnly
          rows={4}
          value={prompt}
          onFocus={(e) => e.currentTarget.select()}
          className="mt-1 w-full rounded-md border border-border bg-background p-2 text-xs"
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => void copy()}
      className="rounded-md border border-border px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted"
    >
      {state === 'copied' ? 'הועתק ✓' : 'העתק בקשה לבדיקת הקובץ'}
    </button>
  );
}

/**
 * The recurring parser complaints, in Hebrew and phrased as something to DO.
 * The raw `reason` is English, written for whoever debugs the pipeline; an admin
 * reading it mid-upload needs to know which sheet to open. Anything unmatched
 * falls back to the original text rather than being hidden.
 */
const REASON_HE: { match: RegExp; he: string }[] = [
  { match: /No בנצ'מרק sheets found/, he: 'לא נמצאו גיליונות תשואות — ללקוח לא יהיו נתוני תשואה כלל' },
  { match: /No ריכוז sheets found/, he: 'לא נמצאו גיליונות ריכוז — ללקוח לא יהיו נתוני החזקות כלל' },
  { match: /Rikuz sheet found but no sleeve labels matched/, he: 'גיליון ריכוז נמצא אך שמות האפיקים בו לא זוהו — בדקו את כותרות האפיקים בגיליון' },
  { match: /Invalid linkage value for index position (\d+)/, he: 'ערך הצמדה לא תקין למדד במיקום $1 — מותר 0-4 (0=ללא, 1=שקל, 2=דולר, 3=אירו, 4=ין)' },
  { match: /Missing index display name \(row 12\) for position (\d+)/, he: 'חסר שם תצוגה למדד במיקום $1 (שורה 12) — נוסחת ה-HLOOKUP בגיליון כנראה לא הצליחה' },
  { match: /No mapping for index_col_in_master/, he: 'למדד בגיליון אין התאמה בקובץ מדדי הייחוס המרכזי' },
  { match: /Sheet skipped: contains '\.\.'/, he: "גיליון דולג — שמו מכיל '..' (סימון גרסה ישנה)" },
  { match: /Found (\d+)\/(\d+) sleeve labels/, he: 'נמצאו $1 מתוך $2 אפיקים בגיליון — האפיקים הבאים לא זוהו' },
  { match: /No report date in the first 8 rows/, he: 'לא נמצא תאריך דוח בגיליון (8 שורות / 13 עמודות ראשונות) — השורה נשמרת ללא תאריך, וגיליון נוסף ללא תאריך ידרוס אותה' },
];

/**
 * Issues that name something we expected in the sheet and did not find get the
 * missing items listed as chips — `raw_value` carries them, and reading a
 * comma-joined list out of a sentence is exactly the part that was unreadable.
 */
const MISSING_FIELDS = new Set(['sleeve_columns']);

function MissingChips({ value }: { value: string }) {
  const items = value.split('·').map((s) => s.trim()).filter(Boolean);
  if (items.length === 0) return null;
  return (
    <ul className="mt-2 flex flex-wrap gap-1.5">
      {items.map((item) => (
        <li
          key={item}
          className="rounded-full border border-dashed border-muted-foreground/40 bg-background/60 px-2 py-0.5 text-xs text-muted-foreground"
        >
          {item}
        </li>
      ))}
    </ul>
  );
}

function explain(reason: string): string | null {
  for (const { match, he } of REASON_HE) {
    const m = reason.match(match);
    if (m) return he.replace(/\$(\d)/g, (_, d) => m[Number(d)] ?? '');
  }
  return null;
}

/**
 * Data-quality issues, per client.
 *
 * The flat table mixed every client together and rendered English reasons in an
 * RTL cell, so they came out scrambled and unreadable. This scopes to the client
 * picked in the header (the thing an admin is actually looking at after an upload), folds identical
 * repeated complaints into a ×N, and leads with a Hebrew explanation — the raw
 * text stays available underneath, since it is what the pipeline actually said.
 */
/**
 * Some problems belong to no single client — a missing benchmark table, no
 * active indices. They were filtered out entirely, so the red badge counted
 * them and no screen could show them: the count said 4 while only 2 were
 * reachable. They get their own switch at the top instead.
 */
const GLOBAL = '__global__';

/** The header's client, plus the problems that belong to no client — both can
 *  be reached from here, so the side-nav badge counts both. */
function DataQualityCard({ clientId, issues }: { clientId: string; issues: IssueView[] }) {
  const own = issues.filter((i) => i.client === clientId);
  const globals = issues.filter((i) => !i.client);
  const [scope, setScope] = useState<string>('');
  const [severity, setSeverity] = useState<'all' | 'error' | 'warn' | 'info'>('all');
  // Open on the general list only when that is where the errors are and the
  // client has none — otherwise the red badge would point at an empty screen.
  const errorsIn = (list: IssueView[]) => list.filter((i) => i.severity === 'error').length;
  const effective = scope || (errorsIn(own) === 0 && errorsIn(globals) > 0 ? GLOBAL : clientId);

  const mine = effective === GLOBAL ? globals : own;
  const counts = { error: 0, warn: 0, info: 0 };
  for (const i of mine) counts[i.severity] += 1;

  // Fold repeats: the same reason for the same fund is one line with a count.
  const folded = new Map<string, { issue: IssueView; n: number }>();
  for (const i of mine) {
    if (severity !== 'all' && i.severity !== severity) continue;
    const k = `${i.severity}|${i.fund ?? ''}|${i.reason}`;
    const hit = folded.get(k);
    if (hit) hit.n += 1;
    else folded.set(k, { issue: i, n: 1 });
  }
  const rows = [...folded.values()].sort(
    (a, b) => SEVERITY_ORDER[a.issue.severity] - SEVERITY_ORDER[b.issue.severity],
  );
  // Grouped under headings rather than run together. A mixed list of a dozen
  // cards, each tinted by its own severity, reads as one alarming block; the
  // heading carries the severity so the cards themselves do not have to.
  const groups = (['error', 'warn', 'info'] as const)
    .map((sev) => ({ sev, items: rows.filter((r) => r.issue.severity === sev) }))
    .filter((g) => g.items.length > 0);

  return (
    <Card>
      {issues.length === 0 ? (
        <EmptyState label="אין בעיות ללקוח הזה — נקי ✓" />
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            {globals.length > 0 && (
              <div className="flex rounded-lg border border-border p-0.5 text-sm">
                {([
                  [clientId, 'של הלקוח', own.length],
                  [GLOBAL, 'כללי — לא משויך ללקוח', globals.length],
                ] as const).map(([id, label, n]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => { setScope(id); setSeverity('all'); }}
                    className={cn(
                      'rounded-md px-3 py-1 transition-colors',
                      effective === id ? 'bg-muted font-semibold' : 'text-muted-foreground hover:bg-muted/60',
                    )}
                  >
                    {label} ({n})
                  </button>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              {(['error', 'warn', 'info'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  disabled={counts[s] === 0}
                  onClick={() => setSeverity(severity === s ? 'all' : s)}
                  className={cn(
                    'rounded-full px-3 py-1 text-xs font-semibold transition-colors disabled:opacity-40',
                    severity === s ? 'ring-2 ring-current ring-offset-1' : '',
                    s === 'error' ? 'bg-destructive/15 text-destructive'
                      : s === 'warn' ? 'bg-amber-500/15 text-amber-700'
                      : 'bg-muted text-muted-foreground',
                  )}
                >
                  {counts[s]} {SEVERITY_LABEL[s]}
                </button>
              ))}
            </div>
          </div>

          {rows.length === 0 ? (
            <EmptyState label={effective === GLOBAL ? 'אין בעיות כלליות ✓' : 'אין בעיות ללקוח הזה ✓'} />
          ) : (
            <div className="flex flex-col gap-6">
              {groups.map(({ sev, items }) => (
                <section key={sev}>
                  <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
                    <span className={cn('h-2 w-2 rounded-full', SEVERITY_DOT[sev])} />
                    {SEVERITY_LABEL[sev]}
                    <span className="font-normal text-muted-foreground">({items.length})</span>
                  </h3>
                  <ul className="flex flex-col gap-2">
              {items.map(({ issue, n }, idx) => {
                const he = explain(issue.reason);
                return (
                  <li
                    key={`${issue.issue_id}-${idx}`}
                    className={cn(
                      // One neutral card, with severity as a thin edge. The
                      // tinted fills made a normal upload look like a disaster.
                      'overflow-hidden rounded-lg border border-border bg-card p-3 border-s-4',
                      issue.severity === 'error' ? 'border-s-destructive/70'
                        : issue.severity === 'warn' ? 'border-s-warning/70'
                        : 'border-s-border',
                    )}
                  >
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                      {issue.fund && <span className="font-medium">{issue.fund}</span>}
                      {n > 1 && <span className="text-xs text-muted-foreground">×{n}</span>}
                    </div>
                    <p className="mt-1 text-sm">{he ?? issue.reason}</p>
                    {MISSING_FIELDS.has(issue.field) && issue.raw_value && (
                      <MissingChips value={issue.raw_value} />
                    )}
                    {/* The line above says what is wrong; this one says what to
                        change in the workbook. It leads, because it is the only
                        part that gets the upload working. Issues we have not
                        written an instruction for show nothing here rather than
                        filler — see ingestAdvice.ts. */}
                    {issue.fix && (
                      <p className="mt-2 rounded-md bg-muted/50 px-3 py-2 text-sm">
                        <span className="font-semibold">מה לעשות: </span>
                        {issue.fix}
                      </p>
                    )}
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                      {issue.check_prompt && <CopyCheckPrompt prompt={issue.check_prompt} />}
                      <IdText>{issue.transform}</IdText>
                      {he && (
                        // The pipeline's own wording, kept for when the Hebrew is
                        // not specific enough — LTR so it does not scramble.
                        <span dir="ltr" className="break-words text-left text-xs text-muted-foreground">
                          {issue.reason}
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </>
      )}
    </Card>
  );
}

const SEVERITY_ORDER = { error: 0, warn: 1, info: 2 } as const;

/** Compact badge for an ingest job's kind (master workbook / monthly PDFs / benchmark). */
function JobKindBadge({ kind }: { kind: string }) {
  const label = kind === 'master' ? 'מאסטר' : kind === 'pdfs' || kind === 'pdf' ? 'PDF חודשי' : kind === 'benchmark' ? 'מדד ייחוס' : kind;
  return (
    <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">{label}</span>
  );
}
